import { useQuery } from '@tanstack/react-query';
import { SearchIcon } from 'lucide-react';
import { useCallback, useId, useMemo } from 'react';

import { Empty, Panel, TabSkeleton } from '@/components/panel/panels';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { TabError } from '@/screens/user-detail/TabError';

import { MethodChip } from './MethodChip';
import { RequestPanel } from './RequestPanel';
import { openApiQueryOptions } from './api-console-query';
import type { ApiConsoleSearch } from './api-console-search';
import { toOperations, type ApiOperation } from './operations';

export interface ApiConsoleScreenProps {
  readonly search: ApiConsoleSearch;
  readonly onSearchChange: (next: ApiConsoleSearch) => void;
}

/**
 * The developer console: pick an endpoint from the API's own OpenAPI document
 * and send a request against it.
 *
 * The imported design offers an "Impersonate user" toggle. It is not built
 * (design spec §11) and there is nothing here to build it with: requests go
 * through the console's own client, carrying the signed-in operator's token,
 * so whatever this sends the audit log names the person who sent it.
 */
export function ApiConsoleScreen({ search, onSearchChange }: ApiConsoleScreenProps) {
  const { api } = useAuth();
  const filterId = useId();
  const query = useQuery(openApiQueryOptions(api));

  const operations = useMemo(() => (query.data === undefined ? [] : toOperations(query.data)), [query.data]);

  const visible = useMemo(() => {
    const needle = search.q?.toLowerCase();
    if (needle === undefined) return operations;
    return operations.filter(
      (operation) =>
        operation.path.toLowerCase().includes(needle) ||
        operation.summary.toLowerCase().includes(needle) ||
        operation.tag.toLowerCase().includes(needle),
    );
  }, [operations, search.q]);

  const selected = operations.find((operation) => operation.id === search.op);

  const onSelectOperation = useCallback(
    (op: string) => {
      onSearchChange({ ...search, op });
    },
    [search, onSearchChange],
  );

  if (query.isPending) return <TabSkeleton rows={5} />;
  if (query.isError) {
    return <TabError message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  // Narrowed by the two guards above: a settled, non-error query has data.
  const { info } = query.data;

  return (
    <div className="grid max-w-[1200px] grid-cols-1 items-start gap-3 xl:grid-cols-[1.2fr_1fr]">
      <Panel
        title="Operations"
        description={`${info.title} ${info.version}, read from the API this console is pointed at rather than from a copy bundled at build time. Requests are sent as you: there is no impersonation, so anything you do here is audited under your own name.`}
        bleed
      >
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-[18px] py-2.5">
          <Label htmlFor={filterId} className="sr-only">
            Filter operations
          </Label>
          <span className="relative inline-flex items-center">
            <SearchIcon
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 size-3.5 text-muted-foreground"
            />
            <Input
              id={filterId}
              type="search"
              value={search.q ?? ''}
              placeholder="Filter by path or summary"
              onChange={(event) => {
                const value = event.target.value.trim();
                onSearchChange({ ...search, q: value === '' ? undefined : value });
              }}
              className="h-[30px] w-[250px] pl-[30px] text-[13px]"
            />
          </span>
          <span className="ml-auto font-mono text-xs text-muted-foreground">
            {visible.length} of {operations.length}
          </span>
        </div>

        {visible.length === 0 ? (
          <div className="border-t border-border">
            <Empty
              title="Nothing matches"
              body="No operation in this document has that in its path, summary or tag."
            />
          </div>
        ) : (
          <ul aria-label="Endpoints" className="flex max-h-[620px] flex-col overflow-y-auto">
            {visible.map((operation) => (
              <li key={operation.id}>
                <button
                  type="button"
                  aria-current={operation.id === search.op ? 'true' : undefined}
                  onClick={() => {
                    onSelectOperation(operation.id);
                  }}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-[11px] border-t border-border px-[18px] py-2.5 text-left hover:bg-secondary',
                    operation.id === search.op && 'bg-secondary',
                  )}
                >
                  <MethodChip method={operation.method} />
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="block truncate font-mono text-[13px] text-foreground">
                      {operation.path}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">{operation.summary}</span>
                  </span>
                  {/*
                    Who may call it, from the route's own guards. Worth seeing
                    before pressing Send: `support` reads this console too, and
                    the audited reveal is the one call that leaves a permanent
                    record of the operator.
                  */}
                  {operation.role === undefined ? null : (
                    <span className={cn('ml-auto shrink-0 text-xs', roleClass(operation.role))}>
                      {operation.role}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {selected === undefined ? (
        <Panel title="Request">
          <Empty
            title="Pick an endpoint"
            body="Choose an operation on the left to fill in its parameters and send it."
          />
        </Panel>
      ) : (
        <RequestPanel key={selected.id} operation={selected} />
      )}
    </div>
  );
}

/** Admin-audited is the one worth reading twice, so it is the one in colour. */
function roleClass(role: NonNullable<ApiOperation['role']>): string {
  if (role === 'admin-audited') return 'text-warn-fg';
  if (role === 'admin') return 'text-foreground';
  return 'text-muted-foreground';
}
